import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { addRenting, getRentings } from "../../../api/rentingsAPI";
import { RentingStatus, SearchType } from "../../../app/constants";
import { addFilterWithRange } from "../../../app/utils";
import moment from "moment";

export const archivedRentingsSlice = createSlice({
  name: "archivedRentings",
  initialState: {
    rentings: [],
    searchValue: "",
    selectedFilter: {
      status: null,
      startDate: null,
      endDate: null,
    },
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "createdAt",
    successMessage: "",
    errorMessage: "",
    selectedId: null,
    selectedRenting: null,
    recreateModalOpen: false,
    filterModalOpen: false,
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setRentings: (state, action) => {
      state.rentings = action.payload;
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
    setSelectedRenting: (state, action) => {
      state.selectedRenting = action.payload;
    },
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
    },
    showRecreateModal: (state, action) => {
      state.recreateModalOpen = action.payload;
    },
    showFilterModal: (state, action) => {
      state.filterModalOpen = action.payload;
    },
  },
});

export const {
  showFilterModal,
  setSelectedFilter,
  setSelectedRenting,
  setSelectedId,
  showRecreateModal,
  setSearchValue,
  setPage,
  setRowsPerPage,
  setPagination,
  setSort,
  setSortBy,
  setRentings,
  setSuccessMessage,
  setErrorMessage,
} = archivedRentingsSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.vendor.value)`
export const selectState = (state) => state.archivedRentings;

export const handleGetRentings = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    let filterParamsArray = [];
    const { selectedFilter } = state.archivedRentings;
    addFilterWithRange(
      filterParamsArray,
      "createdAt",
      selectedFilter.startDate ? moment(selectedFilter.startDate).format("YYYY-MM-DD") : null,
      selectedFilter.endDate ? moment(selectedFilter.endDate).format("YYYY-MM-DD") : null,
    );
    let filterParams = filterParamsArray.join(",");

    const response = await getRentings(
      state.archivedRentings.page,
      state.archivedRentings.rowsPerPage,
      state.archivedRentings.sort,
      state.archivedRentings.sortBy,
      selectedFilter.status
        ? selectedFilter.status
        : `${RentingStatus.TERMINATED},${RentingStatus.CANCELLED}`,
      state.archivedRentings.searchValue,
      SearchType.DEFAULT,
      filterParams
    );
    const { content, ...pagination } = response.data;

    dispatch(setPagination(pagination));
    dispatch(setRentings(content));
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleRecreateRenting = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const payload = {
      requestedParkingAreaId:
        state.archivedRentings.selectedRenting.requestedParkingAreaId,
      startDate: state.archivedRentings.selectedRenting.startDate,
      requestedMonthlySellPrice:
        state.archivedRentings.selectedRenting.requestedMonthlySellPrice,
      timeZoneId: state.archivedRentings.selectedRenting.timeZoneId,
      vehicle: state.archivedRentings.selectedRenting.vehicle,
      customer: state.archivedRentings.selectedRenting.customer,
      additionalInformation:
        state.archivedRentings.selectedRenting.additionalInformation,
      affiliateCampaignId:
        state.archivedRentings.selectedRenting.affiliateCampaignId,
      affiliateCampaignParkingAreaId:
        state.archivedRentings.selectedRenting.affiliateCampaignParkingAreaId,
      includeInsurance: state.archivedRentings.selectedRenting.includeInsurance,
      includeDelivery: state.archivedRentings.selectedRenting.includeDelivery,
      adminFeePaid: state.archivedRentings.selectedRenting.adminFeePaid,
      depositPaid: state.archivedRentings.selectedRenting.depositPaid,
      paid: state.archivedRentings.selectedRenting.paid,
    };
    await addRenting(payload);
    dispatch(showRecreateModal(false));
    dispatch(setSuccessMessage("Successfully recreated renting!"));
    dispatch(handleGetRentings());
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export default archivedRentingsSlice.reducer;
